import {
  useCallback,
  useEffect,
  useRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  conversationMessageAttachmentKey,
  conversationMessageCachedAttachmentImageSize,
  getBrowserElementInspectionPresentation,
  conversationMessageRenderIdentity,
  type ConversationAttachmentImageLayoutHandler,
  type ConversationMessage,
} from '../../types/conversation-message';
import type { ChatElementInspectionPresentationPayload } from '@/jsb';
import { cn } from '@/utils/cn';
import { animate } from 'motion';
import { LinkifiedText } from '../linkified-text';
import { MaskedScrollArea } from '../masked-scrollarea';
import { collectUserMessageContent, CopyButton, copyUserMessage } from './copy';
import { TextAlignleftSFSymbolMedium } from '@/assets/sf-symbols/medium/text.alignleft';
import {
  focusQuoteInContainer,
  type QuoteSelection,
} from '@/utils/chat-quote-dom';
import { ShippingboxSFSymbolMedium } from '@/assets/sf-symbols/medium/shippingbox';
import { ListBulletCircleSFSymbolMedium } from '@/assets/sf-symbols/medium/list.bullet.circle';
import { WrenchAndScrewdriverSFSymbolMedium } from '@/assets/sf-symbols/medium/wrench.and.screwdriver';

import { Menu } from '@/utils/webview-context-menu';
import { useT } from '@/utils/i18n';
import { UserMessageMinimap, useMinimapOptions } from '../minimap';
import { ArrowTurnDownRightSFSymbolMedium } from '@/assets/sf-symbols/medium/arrow.turn.down.right';
import { ConversationAttachmentView } from './conversation-attachment-view';
import { MemoryAttachmentCard } from './memory-attachment';
import { ElementInspectionContextView } from './element-inspection-context';
import { MessageIdentityHeader } from './message-identity';

type InlineCommandTag =
  | {
      kind: 'skill';
      displayName: string;
      originalTag: string;
      sourceRepo?: string;
    }
  | {
      kind: 'plugin';
      displayName: string;
      originalTag: string;
      pluginName: string;
    }
  | {
      kind: 'book';
      displayName: string;
      originalTag: string;
    };

const INLINE_COMMAND_TAG_REGEX =
  /<use-skill(?:\s+display-name="([^"]*)")?(?:\s+source-repo="([^"]*)")?>([^<]+)<\/use-skill>|<use-plugin(?:\s+display-name="([^"]*)")?>([^<]+)<\/use-plugin>|<book>([^<]+)<\/book>/g;

function decodeInlineTagValue(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function inlineCommandTagFromMatch(match: RegExpExecArray): InlineCommandTag {
  const originalTag = match[0];

  if (match[6] !== undefined) {
    return {
      kind: 'book',
      displayName: match[6],
      originalTag,
    };
  }

  if (match[5] !== undefined) {
    const pluginName = match[5].trim();
    return {
      kind: 'plugin',
      displayName: match[4]
        ? decodeInlineTagValue(match[4])
        : decodeInlineTagValue(pluginName),
      originalTag,
      pluginName,
    };
  }

  return {
    kind: 'skill',
    displayName: match[1]
      ? decodeInlineTagValue(match[1])
      : decodeInlineTagValue(match[3]),
    originalTag,
    sourceRepo: match[2],
  };
}

// Parse text and render inline command tags as styled badges while preserving the original tag text for quote/copy flows.
function renderTextWithInlineTags(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = INLINE_COMMAND_TAG_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        <LinkifiedText
          key={keyIndex++}
          text={text.slice(lastIndex, match.index)}
        />
      );
    }
    parts.push(
      renderInlineCommandTag(inlineCommandTagFromMatch(match), keyIndex++)
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(<LinkifiedText key={keyIndex++} text={text.slice(lastIndex)} />);
  }

  return parts.length > 0 ? parts : [<LinkifiedText key={0} text={text} />];
}

function renderInlineCommandTag(tag: InlineCommandTag, key: number): ReactNode {
  if (tag.kind === 'book') {
    return (
      <InlineTagBadge
        key={key}
        originalTag={tag.originalTag}
        className="bg-gray-500/20 text-foreground"
      >
        <ShippingboxSFSymbolMedium
          className="shrink-0 text-[0.6875rem] opacity-75"
          aria-hidden="true"
        />
        {tag.displayName}
      </InlineTagBadge>
    );
  }

  if (tag.kind === 'plugin') {
    return (
      <PluginInlineTagBadge
        key={key}
        displayName={tag.displayName}
        originalTag={tag.originalTag}
        pluginName={tag.pluginName}
      />
    );
  }

  return (
    <InlineTagBadge key={key} originalTag={tag.originalTag}>
      {tag.displayName}
      {tag.sourceRepo && (
        <span className="ml-1 opacity-60">{tag.sourceRepo}</span>
      )}
    </InlineTagBadge>
  );
}

function PluginInlineTagBadge({
  displayName,
  originalTag,
  pluginName,
}: {
  displayName: string;
  originalTag: string;
  pluginName: string;
}) {
  const isLinearPlugin =
    pluginName.trim().toLowerCase() === 'linear' ||
    displayName.trim().toLowerCase() === 'linear';

  if (isLinearPlugin) {
    return (
      <InlineTagBadge
        originalTag={originalTag}
        className="bg-gray-500/20 text-[#1D1D1F] dark:text-[#F5F5F7]"
      >
        <ListBulletCircleSFSymbolMedium
          className="mr-0.5 shrink-0 text-[0.6875rem] opacity-75"
          aria-hidden="true"
        />
        {displayName}
      </InlineTagBadge>
    );
  }

  return (
    <InlineTagBadge originalTag={originalTag}>
      <WrenchAndScrewdriverSFSymbolMedium
        className="shrink-0 text-[0.6875rem] opacity-75"
        aria-hidden="true"
      />
      {displayName}
    </InlineTagBadge>
  );
}

function InlineTagBadge({
  children,
  className,
  originalTag,
}: {
  children: ReactNode;
  className?: string;
  originalTag: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/20 text-xs font-medium relative align-middle whitespace-nowrap',
        className
      )}
    >
      <span
        className="opacity-0 absolute inset-0 overflow-hidden whitespace-nowrap"
        data-quote-ignore="true"
        aria-hidden="true"
      >
        {originalTag}
      </span>
      <span className="inline-flex items-center gap-1 select-none">
        {children}
      </span>
    </span>
  );
}

function renderEmptyTag(text: string): string {
  return text.replace(/<empty\s*\/>/g, '');
}

export const UserMessage = ({
  message,
  elementInspectionPresentations = {},
  enterAnimation = false,
  showIdentity = true,
  onAttachmentImageLayout,
  onSendMessage,
}: {
  message: ConversationMessage;
  elementInspectionPresentations?: Record<
    string,
    ChatElementInspectionPresentationPayload
  >;
  enterAnimation?: boolean;
  showIdentity?: boolean;
  onAttachmentImageLayout?: ConversationAttachmentImageLayoutHandler;
  onSendMessage?: (text: string) => void | Promise<void>;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const minimapOptions = useMinimapOptions();
  const t = useT();

  const { audios, files, images, videos, memories, quotes, texts } =
    collectUserMessageContent(message);
  const parsedElementInspectionPresentation =
    getBrowserElementInspectionPresentation(message);
  const ephemeralElementInspectionPresentation =
    parsedElementInspectionPresentation
      ? elementInspectionPresentations[
          parsedElementInspectionPresentation.inspectionId
        ]
      : undefined;
  const elementInspectionPresentation = parsedElementInspectionPresentation
    ? {
        ...parsedElementInspectionPresentation,
        screenshotDataURL:
          ephemeralElementInspectionPresentation?.screenshotDataURL ??
          parsedElementInspectionPresentation.screenshotDataURL,
        screenshotStatus:
          ephemeralElementInspectionPresentation?.screenshotStatus ??
          parsedElementInspectionPresentation.screenshotStatus,
        updatedAt:
          ephemeralElementInspectionPresentation?.updatedAt ??
          parsedElementInspectionPresentation.updatedAt,
      }
    : undefined;
  const visibleTexts = elementInspectionPresentation ? [] : texts;
  const prompt = texts.join('\n').trim() || undefined;
  const hasVisibleContent =
    !!elementInspectionPresentation ||
    quotes.length > 0 ||
    visibleTexts.length > 0 ||
    files.length > 0 ||
    images.length > 0 ||
    audios.length > 0 ||
    videos.length > 0 ||
    memories.length > 0;
  const canCopyMessage = texts.length > 0;
  if (!hasVisibleContent) {
    return null;
  }

  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const menu = Menu.create().pushItem({
        title: t('Copy'),
        icon: Menu.icon.symbol('document.on.document'),
        enabled: canCopyMessage,
        onClick: async () => {
          await copyUserMessage(message);
        },
      });

      menu.popup(event, { fallbackToSelectionMenu: true });
    },
    [canCopyMessage, message, t]
  );

  const handleFocusQuote = useCallback((quote: QuoteSelection['quoteRef']) => {
    const container =
      ref.current?.closest<HTMLElement>('[data-quote-container="true"]') ??
      null;
    const focusClassName =
      container?.dataset.quoteFocusClass?.trim() ||
      'conversation-message-focused';

    focusQuoteInContainer({
      container,
      quoteRef: quote,
      focusClassName,
    });
  }, []);

  return (
    <div
      ref={ref}
      onContextMenu={handleContextMenu}
      className={cn(
        'user-message',
        'w-full flex pl-4 sm:pl-10 justify-end min-w-0',
        'pt-1',
        'group'
      )}
    >
      <MessageAnimationWrapper
        enabled={enterAnimation}
        className="conversation-message-focus-target flex max-w-full flex-col items-end gap-2"
        data-message-id={message.id}
      >
        {showIdentity ? (
          <MessageIdentityHeader message={message} align="right" />
        ) : null}
        {/* Contexts */}
        <div className="flex max-w-full flex-col items-end gap-2">
          {elementInspectionPresentation && (
            <ElementInspectionContextView
              messageId={message.messageId ?? message.id}
              presentation={elementInspectionPresentation}
            />
          )}

          {memories.length > 0 && (
            <div className="flex flex-col gap-2 items-end">
              {memories.map((memory, i) => (
                <MemoryAttachmentCard
                  key={`memory-${i}`}
                  content={memory.content}
                />
              ))}
            </div>
          )}

          {files.length > 0 && (
            <div
              className={cn(
                'grid gap-2',
                files.length > 1 ? 'grid-cols-2' : ''
              )}
            >
              {files.map((file, i) => (
                <ConversationAttachmentView
                  key={`file-${i}`}
                  content={file.content}
                  previewKind="file"
                  onRequestFileAccess={onSendMessage}
                  className={cn(
                    files.length > 2 &&
                      files.length % 2 !== 0 &&
                      i === files.length - 1 &&
                      'col-start-2'
                  )}
                />
              ))}
            </div>
          )}

          {images.length > 0 && (
            <MaskedScrollArea horizontal className="w-full">
              <div className="flex max-w-full gap-2">
                <div className="flex-1 min-w-0" />
                {images.map((image, i) => {
                  const attachmentKey = conversationMessageAttachmentKey(
                    image.contentIndex
                  );
                  return (
                    <ConversationAttachmentView
                      key={`img-${i}`}
                      content={{ ...image.content, generationPrompt: prompt }}
                      fallbackFileName="Image file"
                      previewKind="image"
                      compactImageActions
                      allowImageDownload={false}
                      cachedImageSize={conversationMessageCachedAttachmentImageSize(
                        message,
                        attachmentKey
                      )}
                      imageClassName="h-24 max-h-24 w-32 shrink-0"
                      onImageLayout={imageSize =>
                        onAttachmentImageLayout?.(
                          conversationMessageRenderIdentity(message),
                          attachmentKey,
                          imageSize
                        )
                      }
                      onRequestFileAccess={onSendMessage}
                    />
                  );
                })}
              </div>
            </MaskedScrollArea>
          )}

          {audios.length > 0 && (
            <div className="flex flex-col gap-2 items-end">
              {audios.map((audio, i) => (
                <ConversationAttachmentView
                  key={`audio-${i}`}
                  content={audio.content}
                  fallbackFileName="Audio file"
                  previewKind="audio"
                  onRequestFileAccess={onSendMessage}
                />
              ))}
            </div>
          )}

          {videos.length > 0 && (
            <div className="flex flex-col gap-2 items-end">
              {videos.map((video, i) => (
                <ConversationAttachmentView
                  key={`video-${i}`}
                  content={video.content}
                  fallbackFileName="Video file"
                  previewKind="video"
                  className="h-48 w-auto max-w-full"
                  onRequestFileAccess={onSendMessage}
                />
              ))}
            </div>
          )}
        </div>
        {/* Message bubble */}
        {quotes.length > 0 && (
          <div className="flex w-full max-w-full flex-col items-end gap-1">
            {quotes.map((quote, index) => (
              <button
                key={`quote-${index}`}
                type="button"
                className="flex max-w-[min(100%,21rem)] items-center gap-2 px-0.5 py-0.5 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => handleFocusQuote(quote.quoteRef)}
              >
                <span className="shrink-0 leading-none">
                  <ArrowTurnDownRightSFSymbolMedium className="text-[0.5rem]" />
                </span>
                <span className="truncate">{quote.text}</span>
              </button>
            ))}
          </div>
        )}
        {visibleTexts.length > 0 && (
          <div
            className={cn(
              'user-message-bubble',
              'bg-user-bubble py-2 px-3 min-w-10 rounded-2xl max-w-full',
              'text-sm whitespace-pre-wrap wrap-break-word space-y-2'
            )}
            data-message-id={message.messageId ?? message.id}
            data-quote-source="true"
          >
            <div className="user-message-bubble-content">
              {visibleTexts.map((text, i) => (
                <div key={`text-${i}`}>
                  {renderTextWithInlineTags(renderEmptyTag(text))}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Actions */}
        <div className="invisible group-hover:visible flex items-center gap-2 justify-end">
          <button
            className="icon-button size-6 flex-center"
            onClick={() =>
              UserMessageMinimap.toggle(message.id, minimapOptions)
            }
          >
            <TextAlignleftSFSymbolMedium className="text-xs" />
          </button>
          <CopyButton
            className="icon-button size-6 flex-center"
            onCopy={() => copyUserMessage(message)}
          />
        </div>
      </MessageAnimationWrapper>
    </div>
  );
};

function waitUntilVisible(el: HTMLElement, timeout = 300) {
  return Promise.race([
    new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, timeout);
    }),
    new Promise<void>(resolve => {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          resolve();
        }
      });
      observer.observe(el);
    }),
  ]);
}

const MessageAnimationWrapper = ({
  className,
  enabled = true,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  enabled?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const applyAnimation = useCallback(async () => {
    const el = ref.current;
    if (!el) return;

    if (!enabled) {
      el.style.opacity = '1';
      return;
    }

    await waitUntilVisible(el);
    await new Promise(resolve => setTimeout(resolve, 100));

    const box = el.getBoundingClientRect();
    const viewportSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const fromXYWH = {
      x: 16,
      y: viewportSize.height + 16,
      w: viewportSize.width / 2,
      h: 32,
    };

    const toXYWH = {
      x: box.left,
      y: box.top,
      w: box.width,
      h: box.height,
    };

    animate(
      el,
      {
        y: [fromXYWH.y - toXYWH.y, 0],
        scaleX: [Math.min(2, fromXYWH.w / toXYWH.w), 1],
        scaleY: [fromXYWH.h / toXYWH.h, 1],
        opacity: [0, 1],
      },
      {
        type: 'spring',
        bounce: 0.2,
        duration: 0.5,
      }
    ).finished.then(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateX(0)';
      el.style.width = 'auto';
      el.style.height = 'auto';
    });
  }, [enabled]);

  useEffect(() => {
    applyAnimation();
  }, [applyAnimation]);

  return (
    <div
      ref={ref}
      className={cn(className, 'opacity-0 origin-right')}
      {...props}
    />
  );
};
